import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Database, Eye, EyeOff } from 'lucide-react';
import './Login.css';
import { validatePassword } from './utils/validation';

const API_ROOT = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || '')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');
axios.defaults.baseURL = API_ROOT;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) {
        if (!cancelled) {
          setChecking(false);
          setValid(false);
        }
        return;
      }
      try {
        const res = await axios.get('/api/auth/reset-password/validate', { params: { token } });
        if (cancelled) return;
        setValid(Boolean(res.data.valid));
        setUsername(res.data.username || '');
      } catch {
        if (!cancelled) setValid(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const passwordError = validatePassword(password, { fieldLabel: 'New password' });
    if (passwordError) {
      toast.error(passwordError);
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post('/api/auth/reset-password', { token, password });
      setDone(true);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrap">
      <ToastContainer theme="light" position="top-right" autoClose={4000} />
      <header className="topbar login-topbar">
        <h1 className="topbar-brand">
          <Database size={24} />
          <span>Client Management</span>
        </h1>
        <Link to="/login" className="login-nav-back">
          ← Back to sign in
        </Link>
      </header>
      <div className="login-body">
        <div className="login-card">
          {checking && <p className="login-subtitle">Validating reset link…</p>}

          {!checking && !valid && (
            <>
              <h2 className="login-title">Link expired</h2>
              <p className="login-subtitle">
                This password reset link is invalid or has expired. Request a new one to continue.
              </p>
              <div className="login-actions">
                <Link to="/forgot-password" className="btn btn-primary login-link-btn">
                  Request new link
                </Link>
              </div>
            </>
          )}

          {!checking && valid && !done && (
            <form onSubmit={handleSubmit} autoComplete="off">
              <h2 className="login-title">Set new password</h2>
              <p className="login-subtitle">
                {username ? `Create a new password for ${username}` : 'Create a new password for your account'}
              </p>
              <div className="form-group">
                <label className="form-label" htmlFor="password">New password</label>
                <div className="login-password-row">
                  <input
                    id="password"
                    className="form-control"
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value.replace(/\s/g, ''))}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    minLength={6}
                    maxLength={128}
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-outline"
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPwd((v) => !v)}
                  >
                    {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="confirmPassword">Confirm password</label>
                <input
                  id="confirmPassword"
                  className="form-control"
                  type={showPwd ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value.replace(/\s/g, ''))}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  minLength={6}
                  maxLength={128}
                  required
                />
                <small className="field-hint">At least 6 characters · no spaces</small>
              </div>
              <div className="login-actions">
                <button type="submit" disabled={loading} className="btn btn-primary">
                  {loading ? 'Saving…' : 'Reset password'}
                </button>
              </div>
            </form>
          )}

          {!checking && valid && done && (
            <>
              <h2 className="login-title">Password updated</h2>
              <p className="login-subtitle">Your password has been reset. You can now sign in with your new password.</p>
              <div className="login-actions">
                <Link to="/login" className="btn btn-primary login-link-btn">
                  Sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
